import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import CustomersCrmStatisticsAPI from '../../../api/CustomersCrmStatisticsAPI';
import LinearGraph from '../../../components/LinearGraph/LinearGraph';
import NoData from '../../../components/NoData/NoData';
import { bucketizeDailySeriesForChart } from '../../../shared/bucketizeDailySeriesForChart';

type Props = {
  timeRange: { start: string; end: string };
};

function CustomersTotalOverTimeLinear({ timeRange }: Props) {
  const { t, i18n } = useTranslation();
  const [dailyLabels, setDailyLabels] = useState<string[]>([]);
  const [dailyValues, setDailyValues] = useState<number[]>([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await CustomersCrmStatisticsAPI.getTotalOverTime({
          startTime: timeRange.start,
          endTime: timeRange.end,
        });
        const rawLabels = res.data?.labels || [];
        setDailyLabels(rawLabels.map((l) => String(l).slice(0, 10)));
        setDailyValues((res.data?.values || []).map((v) => Number(v)));
      } catch {
        setDailyLabels([]);
        setDailyValues([]);
      }
    };
    fetchData();
  }, [timeRange.start, timeRange.end]);

  const { xLabels, values, total } = useMemo(() => {
    const rangeStart = new Date(timeRange.start);
    const rangeEnd = new Date(timeRange.end);
    const { xLabels: xl, values: vals } = bucketizeDailySeriesForChart(
      rangeStart,
      rangeEnd,
      dailyLabels,
      dailyValues,
      i18n.language,
    );
    const sum = vals.reduce((a, b) => a + b, 0);
    return { xLabels: xl, values: vals, total: sum };
  }, [timeRange.start, timeRange.end, dailyLabels, dailyValues, i18n.language]);

  if (total === 0) return <NoData message={t('global.empty_results')} />;

  return (
    <LinearGraph
      title={t('stats.total_over_time') || 'Total over time'}
      xLabels={xLabels}
      showAllXTicks
      dataSets={[
        {
          label: t('stats.customers') || 'Customers',
          data: values,
          borderColor: 'rgba(16, 185, 129, 0.9)',
          backgroundColor: 'rgba(16, 185, 129, 0.2)',
          borderWidth: 2,
        },
      ]}
    />
  );
}

export default CustomersTotalOverTimeLinear;
